/**
 * Map PostgREST / Postgres mutation errors to short operator-facing copy.
 * Prefer never showing constraint names or "violates foreign key" dumps.
 */

type Locale = 'zh-CN' | 'en';

const TABLE_LABELS: Record<string, { zh: string; en: string }> = {
  fleet_operators: { zh: '车队', en: 'fleet' },
  vehicles: { zh: '车辆', en: 'vehicle' },
  drivers: { zh: '司机', en: 'driver' },
  trips: { zh: '行程', en: 'trip' },
  jobs: { zh: '用车订单', en: 'job' },
  job_legs: { zh: '行程段', en: 'job leg' },
  organizations: { zh: '客户组织', en: 'organization' },
  portal_user_profiles: { zh: '登录成员', en: 'portal members' },
  portal_roles: { zh: '角色', en: 'role' },
  portal_role_permissions: { zh: '角色权限', en: 'role permissions' },
  location_logs: { zh: 'GPS 记录', en: 'GPS logs' },
  trip_feedback: { zh: '行程反馈', en: 'trip feedback' },
  sos_events: { zh: 'SOS 事件', en: 'SOS events' },
  vehicle_inspections: { zh: '车辆检查', en: 'vehicle inspections' },
  reminder_types: { zh: '提醒类型', en: 'reminder type' },
  reminder_source_fields: { zh: '提醒字段', en: 'reminder fields' },
  outbound_send_logs: { zh: '发送记录', en: 'send logs' },
  job_lifecycle_events: { zh: '订单履历', en: 'job history' },
};

function label(table: string, locale: Locale): string {
  const row = TABLE_LABELS[table];
  if (!row) return table;
  return locale === 'en' ? row.en : row.zh;
}

function extractMessage(raw: unknown): string {
  if (raw == null) return '';
  if (typeof raw === 'string') return raw.trim();
  if (raw instanceof Error) return raw.message.trim();
  if (typeof raw === 'object' && 'message' in raw) {
    const m = (raw as { message?: unknown }).message;
    if (typeof m === 'string') return m.trim();
  }
  return String(raw).trim();
}

/** Already operator-written (RPC RAISE, i18n, Chinese copy). */
function looksUserFacing(text: string): boolean {
  if (/请先|无法删除|不能删除|已被|仍有|关联|Admin access required/i.test(text)) {
    // Keep Chinese RPC messages; translate generic admin English below.
    if (/^Admin access required$/i.test(text)) return false;
    if (/[\u4e00-\u9fff]/.test(text)) return true;
  }
  if (/[\u4e00-\u9fff]/.test(text) && !/violates|constraint|fkey|foreign key/i.test(text)) {
    return true;
  }
  return false;
}

const FK_ON_TABLE =
  /violates foreign key constraint "[^"]+" on table "([^"]+)"/i;
const FK_REFERENCED =
  /is still referenced from table "([^"]+)"/i;
const FK_UPDATE_DELETE =
  /update or delete on table "([^"]+)" violates foreign key/i;

/**
 * Turn delete/update FK and common DB failures into readable UI text.
 */
export function formatUserFacingDbError(
  raw: unknown,
  opts?: { locale?: Locale; action?: 'delete' | 'update' | 'save' },
): string {
  const locale: Locale = opts?.locale === 'en' ? 'en' : 'zh-CN';
  const action = opts?.action ?? 'delete';
  const text = extractMessage(raw);
  if (!text) {
    return locale === 'en' ? 'Something went wrong. Please try again.' : '操作失败，请稍后重试。';
  }

  if (looksUserFacing(text)) return text;

  if (/^Admin access required$/i.test(text)) {
    return locale === 'en' ? 'Admin access required.' : '需要平台管理员权限。';
  }

  const refMatch = text.match(FK_ON_TABLE) || text.match(FK_REFERENCED);
  const parentMatch = text.match(FK_UPDATE_DELETE);
  if (refMatch || /foreign key|fkey/i.test(text)) {
    const refTable = refMatch?.[1];
    const parentTable = parentMatch?.[1];
    const refLabel = refTable ? label(refTable, locale) : null;
    const parentLabel = parentTable ? label(parentTable, locale) : null;

    if (locale === 'en') {
      if (action === 'delete') {
        if (refLabel && parentLabel) {
          return `Cannot delete this ${parentLabel}: it is still linked to ${refLabel}. Remove or reassign those records first.`;
        }
        if (refLabel) {
          return `Cannot delete: still linked to ${refLabel}. Remove or reassign those records first.`;
        }
        return 'Cannot delete: this record is still in use. Remove related data first.';
      }
      return 'Cannot save: this change conflicts with related records.';
    }

    if (action === 'delete') {
      if (refLabel && parentLabel) {
        return `无法删除该${parentLabel}：仍有关联的${refLabel}。请先处理关联数据后再试。`;
      }
      if (refLabel) {
        return `无法删除：仍有关联的${refLabel}。请先处理关联数据后再试。`;
      }
      return '无法删除：该数据仍被使用。请先处理关联数据后再试。';
    }
    return '无法保存：与关联数据冲突，请检查后重试。';
  }

  if (/violates check constraint|Failing row contains|new row for relation/i.test(text)) {
    return locale === 'en'
      ? 'Cannot save: data failed validation. Check required fields and try again.'
      : '无法保存：数据不符合规则，请检查必填项后重试。';
  }

  if (/duplicate key|unique constraint/i.test(text)) {
    return locale === 'en'
      ? 'Cannot save: a record with the same value already exists.'
      : '无法保存：已存在相同数据，请修改后重试。';
  }

  // Strip ultra-technical dumps; keep short unknown messages under length cap.
  if (/constraint|pg_|SQLSTATE|PGRST/i.test(text) || text.length > 180) {
    return locale === 'en'
      ? 'Operation failed. Please try again or contact support.'
      : '操作失败，请稍后重试。如需帮助请联系管理员。';
  }

  return text;
}
